/**
 * Debug Service for Web
 *
 * Exposes debug endpoints via window.__switchDebug for E2E testing.
 * This matches the debug HTTP server functionality on iOS and Android.
 *
 * Usage in tests:
 *   const result = await window.__switchDebug.searchUser("john.1234");
 *   const currentUser = await window.__switchDebug.currentUser();
 */

import { useStore } from "@/state/legacy_module_api";
import { api_find_user } from "@/core_bridge/module_api";
import { createDirectThreadKey, createGroupThreadKey, createSelfThreadKey } from "@/core_bridge/thread_keys";
import { parseJwt } from "@/state/legacy_module_api";
import {
  api as coreApi,
  attachments,
  crypto as coreCrypto,
  initSwitchCore,
  setToken,
  initializeWebSocket,
  wasm,
  parseThreadId,
  isGroupId,
  generateUUID,
  groups as coreGroups,
  MessageType,
  ThreadType,
  handleIncomingMessageJson,
  type DebugAppScreen,
  type Contact,
  type Member,
  type Thread,
} from "@/core_bridge/module_api";
import {
  createCommunityChannelConversationLane,
  createGroupConversationLane,
} from "@/core_bridge/switch-core";
import { appConfig } from "@/app/module_api";
import { performClientLogout } from "@/state/logout";
import { connect as connectWebSocket, disconnect as disconnectWebSocket } from "@/transport/webSocketService";
import { resolveCallRoomId } from "@/services/call";
import { fetchCallToken } from "@/services/call/callToken";
import { db } from "@/services/module_api";
import { services } from "@/services/module_api";
import { resolveContactPublicKey, serializeContactPublicKeys } from "@/data/adapters/contact_keys";
import { messageSchema } from "@/data/schemas/message.schema";
import { addMember as groupAddMember } from "@/features/groups/module_api";
import {
  createCommunity as createCommunityFlow,
  inviteCommunityMember as inviteCommunityMemberFlow,
  updateCommunityMemberRole as updateCommunityMemberRoleFlow,
  leaveCommunity as leaveCommunityFlow,
  createCommunityGroup as createCommunityGroupFlow,
  handleCommunityUpdatedPayload,
} from "@/features/community/communityService";
import { applyLinkedDeviceSession } from "@/features/more/deviceLinking";

const stableObjectUrl = (uploadUrl: string): string => {
  try {
    const url = new URL(uploadUrl);
    url.search = "";
    return url.toString();
  } catch {
    return uploadUrl.split("?")[0] || uploadUrl;
  }
};

// Response types matching iOS/Android debug server format
interface SearchUserResponse {
  found: boolean;
  user: SearchedUser | null;
  error: string | null;
}

interface SearchedUser {
  userId: string;
  username: string;
  discriminator: string;
  publicKey: string | null;
}

interface CurrentUserResponse {
  found: boolean;
  user: CurrentUser | null;
  error: string | null;
}

interface CurrentUser {
  userId: string;
  username: string | null;
  discriminator: string | null;
  publicKey: string | null;
}

interface GroupKeyInfoResponse {
  groupId: string;
  currentKeyVersion: number;
  totalKeys: number;
  keyFingerprint: string;
  lastRotated: string | null;
  keys: Array<{ version: number; fingerprint: string }>;
  error: string | null;
}

interface AppStateResponse {
  route: "register" | "main";
  screen: DebugAppScreen;
  hasToken: boolean;
  userId: string | null;
  username: string | null;
  discriminator: string | null;
  activeThreadId: string | null;
  activeTab?: string | null;
}

interface RegisterResponse {
  success: boolean;
  userId?: string;
  username?: string;
  discriminator?: string;
  error?: string;
}

interface LogoutResponse {
  success: boolean;
  message?: string;
  error?: string;
}

interface ServerConfigResponse {
  apiBaseUrl: string;
  websocketBaseUrl: string;
  appId: string;
}

type CallTransport = "jitsi";

interface CallTransportResponse {
  transport: CallTransport;
  supportedTransports: CallTransport[];
}

interface DebugAPI {
  /** Search for a user by full handle (username.discriminator) */
  searchUser: (handle: string) => Promise<SearchUserResponse>;

  /** Get the currently logged-in user */
  currentUser: () => Promise<CurrentUserResponse>;

  /** Get all contacts from local storage */
  contacts: () => Promise<any[]>;

  /** Get all threads from local storage */
  threads: () => Promise<any[]>;

  /** Get all messages from local storage */
  messages: (threadKey?: string) => Promise<any[]>;

  /** Get COMMUNITY_UPDATED messages (optional communityId filter) */
  communityUpdates: (communityId?: string) => Promise<any[]>;

  /** Reprocess a COMMUNITY_UPDATED message by messageId (or latest for communityId) */
  reprocessCommunityUpdate: (input: {
    messageId?: string;
    communityId?: string;
    forceContactRefresh?: boolean;
  }) => Promise<any>;

  /** Get all communities from local storage */
  communities: () => Promise<any[]>;

  /** Get current auth token info */
  token: () => { hasToken: boolean; claims: any | null; rawToken?: string | null };

  /** Get local encryption/key status */
  encryptionStatus: () => {
    hasKeyPair: boolean;
    publicKey: string | null;
    secretKeyPresent: boolean;
    publicKeyFingerprint: string | null;
  };

  /** Get websocket connection status */
  websocketStatus: () => {
    state: string;
    lastError: string | null;
    lastMessage: string | null;
    acknowledgedMessageCount: number;
  };

  /** Force-close WebSocket for reconnect/offline correctness tests */
  websocketDisconnect: () => { success: boolean; state: string };

  /** Reconnect WebSocket for reconnect/offline correctness tests */
  websocketConnect: () => Promise<{ success: boolean; state: string; error?: string }>;

  /** Get group key info for conformance testing */
  getGroupKeyInfo: (groupId: string) => Promise<GroupKeyInfoResponse>;

  /** Get all data combined */
  all: () => Promise<{
    token: { hasToken: boolean; claims: any | null };
    users: { count: number; data: any[] };
    threads: { count: number; data: any[] };
    messages: { count: number; data: any[] };
  }>;

  /** Get app state (route, token status, user info) */
  appState: () => AppStateResponse;

  /** Register a new user with username */
  register: (username: string) => Promise<RegisterResponse>;

  /** Start a device-link session from an authenticated device */
  deviceLinkStart: () => Promise<{
    success: boolean;
    code?: string;
    linkId?: string;
    expiresAt?: number;
    error?: string;
  }>;

  /** Complete a device-link session on this device using one-time code */
  deviceLinkComplete: (code: string, platform?: string) => Promise<{
    success: boolean;
    token?: string;
    deviceId?: string;
    status?: string;
    userId?: string;
    username?: string | null;
    discriminator?: string | null;
    error?: string;
  }>;

  /** List linked devices for the authenticated account */
  listDevices: () => Promise<{
    success: boolean;
    devices?: any[];
    error?: string;
  }>;

  /** Get the effective server/app config used by this runtime */
  serverConfig: () => ServerConfigResponse;

  /** Dispatch a core-defined debug action */
  dispatch: (action: unknown) => Promise<any>;

  /** Start an outgoing call for a thread */
  startCall: (
    threadId: string,
    roomId?: string
  ) => Promise<{
    success: boolean;
    threadId?: string;
    roomId?: string;
    transport?: CallTransport;
    error?: string;
  }>;

  /** Get current call status */
  callStatus: () => Promise<{
    callActive: boolean;
    roomId: string | null;
    transport: CallTransport;
  }>;

  /** Get current call transport mode */
  getCallTransport: () => Promise<CallTransportResponse>;

  /** Set call transport mode (jitsi) */
  setCallTransport: (transport: string) => Promise<{
    success: boolean;
    transport?: CallTransport;
    supportedTransports?: CallTransport[];
    error?: string;
  }>;

  /** Logout and clear all data */
  logout: () => Promise<LogoutResponse>;

  /** Create a community */
  createCommunity: (name: string, description?: string) => Promise<any>;

  /** Invite a member to a community */
  inviteCommunityMember: (communityId: string, userId: string, role?: string) => Promise<any>;

  /** Leave a community */
  leaveCommunity: (communityId: string) => Promise<any>;

  /** Update a community member role */
  updateCommunityMemberRole: (communityId: string, userId: string, role: string) => Promise<any>;

  /** Create a community group */
  createCommunityGroup: (
    communityId: string,
    name: string,
    description?: string
  ) => Promise<any>;
}

type NormalizedHandle = {
  trimmed: string;
  baseUsername: string;
  discriminator: string;
};

const COMMUNITY_ROLES = ["member", "moderator", "admin", "owner"] as const;
type CommunityRole = (typeof COMMUNITY_ROLES)[number];
const SUPPORTED_CALL_TRANSPORTS: CallTransport[] = ["jitsi"];
const currentCallTransport: CallTransport = "jitsi";

function normalizeCommunityRole(
  role: string | undefined | null,
  { allowOwner }: { allowOwner: boolean }
): { ok: true; value: CommunityRole | undefined } | { ok: false; error: string } {
  if (!role) {
    return { ok: true, value: undefined };
  }
  const normalized = role.trim().toLowerCase();
  if (!normalized) {
    return { ok: true, value: undefined };
  }
  if (!COMMUNITY_ROLES.includes(normalized as CommunityRole)) {
    return {
      ok: false,
      error: `Invalid role. Expected: ${COMMUNITY_ROLES.join("|")}`,
    };
  }
  if (!allowOwner && normalized === "owner") {
    return { ok: false, error: "Owner role cannot be assigned via this endpoint" };
  }
  return { ok: true, value: normalized as CommunityRole };
}

function normalizeHandle(handle: string): { ok: true; value: NormalizedHandle } | { ok: false; error: string } {
  const trimmed = (handle || "").trim().toLowerCase();
  if (!trimmed) {
    return { ok: false, error: "Missing required field: handle" };
  }
  const lastDotIndex = trimmed.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
    return { ok: false, error: "Invalid username format. Expected: username.discriminator" };
  }
  const baseUsername = trimmed.slice(0, lastDotIndex);
  const discriminator = trimmed.slice(lastDotIndex + 1);
  if (discriminator.length !== 4 || !/^\d{4}$/.test(discriminator)) {
    return { ok: false, error: "Invalid discriminator. Expected 4-digit number" };
  }
  return { ok: true, value: { trimmed, baseUsername, discriminator } };
}

function findContactInStoreByHandle(state: any, handle: string): Contact | null {
  const contactsById = state.contactsById || {};
  const contacts = Object.values(contactsById) as Contact[];
  const match = contacts.find((contact) => {
    const fullHandle = (contact as any).fullHandle || (contact as any).full_handle;
    return typeof fullHandle === "string" && fullHandle.toLowerCase() === handle.toLowerCase();
  });
  return match || null;
}

async function ensureContactAddInvariant(contact: Contact, state: any): Promise<{ threadId: string; contactVisible: boolean; hasPublicKey: boolean }> {
  const myUserId = parseJwt(state.apiToken || "").user_id;
  if (!myUserId) {
    throw new Error("No user logged in");
  }
  if (!contact.userId) {
    throw new Error("Added contact is missing userId");
  }

  const publicKey = resolveContactPublicKey(contact);
  if (!publicKey) {
    throw new Error("Added contact is missing public key material");
  }

  const persisted = (await services.db.getUsers?.())?.find((candidate: any) => candidate?.userId === contact.userId);
  const visible = persisted || state.contactsById?.[contact.userId];
  if (!visible) {
    throw new Error("Added contact is not visible in local contact store");
  }

  const threadId = createDirectThreadKey(myUserId, contact.userId);
  const existingThread = await services.db.getGrp?.(threadId);
  if (!existingThread) {
    const now = Date.now();
    const thread = {
      id: threadId,
      name: contact.fullHandle || contact.username || contact.userId,
      description: null,
      members: [],
      lastMessage: null,
      ownerId: myUserId,
      withUserId: contact.userId,
      createdAt: now,
      updatedAt: now,
      unread: 0,
      isAnnouncementGroup: false,
      isDefaultGroup: false,
      isStandaloneGroup: false,
      type: myUserId === contact.userId ? ThreadType.SELF : ThreadType.DM,
    };
    await services.db.insertGrp?.(thread as any);
    state.setThread?.(thread as any);
  } else {
    state.setThread?.(existingThread as any);
  }

  const verifiedThread = await services.db.getGrp?.(threadId);
  if (!verifiedThread) {
    throw new Error("Canonical DM thread was not created for added contact");
  }

  return { threadId, contactVisible: true, hasPublicKey: true };
}

async function sendContactAddFanout(contact: Contact, state: any): Promise<void> {
  try {
    const myUserId = parseJwt(state.apiToken || "").user_id;
    const displayPhotoUrl =
      state.contactsById?.[myUserId]?.displayPhoto ||
      (state as any).profilePhotos?.[myUserId]?.url ||
      state.myProfilePhoto;
    const displayPhotoKey = state.displayPhotoKey;
    const theirPublicKey = resolveContactPublicKey(contact);

    if (myUserId && displayPhotoUrl && displayPhotoKey && theirPublicKey) {
      await initSwitchCore();
      const prepared = (coreApi as any).prepareContactAddMessage(
        myUserId,
        displayPhotoUrl,
        displayPhotoKey,
        contact.userId,
        theirPublicKey,
        myUserId,
        state.keyPair?.base64?.secretKey || (state.keyPair?.base64 as any)?.secret_key || ""
      );
      try {
        await services.threadService.sendMessage?.(prepared);
      } catch (err) {
        console.warn("[Debug] contact-add fanout send failed", err);
      }
    }
  } catch (err) {
    console.warn("[Debug] contact-add fanout skipped", err);
  }
}

async function addContactByHandleAction(handle: string): Promise<any> {
  const normalized = normalizeHandle(handle);
  if (normalized.ok === false) {
    return { success: false, error: normalized.error };
  }

  const state = useStore.getState();
  if (!state.apiToken) {
    return { success: false, error: "No user logged in" };
  }

  try {
    const result = await api_find_user(normalized.value.trimmed);
    if (!result || !(result as any).userId) {
      return { success: false, error: "User not found" };
    }

    const userData = (result as any).user || {};
    const rawPublicKeys = userData.publicKeys || userData.public_keys || [];
    const contactDeviceId =
      userData.deviceId ||
      userData.device_id ||
      userData.primaryDeviceId ||
      userData.primary_device_id ||
      (Array.isArray(rawPublicKeys) ? rawPublicKeys[0]?.deviceId || rawPublicKeys[0]?.device_id : undefined);
    const normalizedPublicKeys = (Array.isArray(rawPublicKeys) ? rawPublicKeys : [])
      .map((entry: any) => {
        const publicKey = entry?.publicKey || entry?.public_key;
        if (!publicKey) return null;
        return {
          publicKey: String(publicKey).replace(/\s+/g, ""),
          deviceId: entry?.deviceId || entry?.device_id || contactDeviceId,
          mlKemPublicKey: entry?.mlKemPublicKey || entry?.ml_kem_public_key || undefined,
        };
      })
      .filter(Boolean);
    const legacyPublicKey =
      userData.publicKey ||
      userData.public_key ||
      normalizedPublicKeys[0]?.publicKey ||
      null;
    const serializedPublicKeys =
      serializeContactPublicKeys(normalizedPublicKeys as any, contactDeviceId) || legacyPublicKey;

    const contact = {
      userId: (result as any).userId,
      username: (result as any).username || normalized.value.baseUsername,
      discriminator: (result as any).discriminator || normalized.value.discriminator,
      fullHandle:
        userData.fullHandle ||
        userData.full_handle ||
        normalized.value.trimmed,
      threadKey: userData.threadKey || userData.thread_key || null,
      publicKey: serializedPublicKeys,
      publicKeys: normalizedPublicKeys as any,
      deviceId: contactDeviceId,
      displayPhoto: userData.displayPhoto || null,
      displayPhotoMedia: userData.displayPhotoMedia || null,
      coverPhoto: userData.coverPhoto || null,
      status: userData.status || null,
      bio: userData.bio || null,
      lastSeen: userData.lastSeen || null,
      isBlocked: userData.isBlocked ?? userData.isBlock ?? false,
      isMuted: userData.isMuted ?? userData.isMute ?? false,
      createdAt: userData.createdAt || null,
      updatedAt: userData.updatedAt || null,
    } as any as Contact;

    if (
      contact.displayPhoto &&
      contact.displayPhoto.endsWith(".enc") &&
      !(contact as any).displayPhotoKey
    ) {
      contact.displayPhoto = null;
    }

    state.setUser?.(contact as any);
    await services.db.insertUser(contact as any);
    const invariant = await ensureContactAddInvariant(contact, state);
    await sendContactAddFanout(contact, state);

    return {
      success: true,
      threadId: invariant.threadId,
      invariant,
      user: {
        userId: contact.userId,
        username: contact.username,
        discriminator: contact.discriminator,
        fullHandle: contact.fullHandle,
        displayPhoto: contact.displayPhoto,
        publicKey: contact.publicKey,
      },
      contact,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to add contact" };
  }
}

async function resolveContactByHandle(
  handle: string,
  { allowAdd }: { allowAdd: boolean }
): Promise<{ contact: Contact | null; error?: string }> {
  const normalized = normalizeHandle(handle);
  if (normalized.ok === false) {
    return { contact: null, error: normalized.error };
  }

  const state = useStore.getState();
  const existing = findContactInStoreByHandle(state, normalized.value.trimmed);
  if (existing) {
    return { contact: existing };
  }

  if (!allowAdd) {
    return { contact: null, error: "Contact not found" };
  }

  const addResult = await addContactByHandleAction(normalized.value.trimmed);
  if (addResult?.success && addResult?.contact) {
    return { contact: addResult.contact as Contact };
  }
  return { contact: null, error: addResult?.error || "Failed to resolve contact" };
}

async function createDirectThreadByHandleAction(handle: string): Promise<any> {
  const resolved = await resolveContactByHandle(handle, { allowAdd: false });
  if (!resolved.contact) {
    return { success: false, error: resolved.error || "Contact not found" };
  }

  const state = useStore.getState();
  const myUserId = parseJwt(state.apiToken || "").user_id;
  if (!myUserId) {
    return { success: false, error: "No user logged in" };
  }

  state.setActiveTab?.("/threads-tab");
  state.selectThread?.({ withUserId: resolved.contact.userId });

  const threadId = createDirectThreadKey(myUserId, resolved.contact.userId);

  return {
    success: true,
    threadId,
    withUserId: resolved.contact.userId,
  };
}

function openThreadAction(threadId: string): any {
  if (!threadId) {
    return { success: false, error: "Missing threadId" };
  }

  const state = useStore.getState();
  state.setActiveTab?.("/threads-tab");

  if (threadId.startsWith("group:") || isGroupId(threadId)) {
    const groupId = threadId.startsWith("group:") ? threadId.replace("group:", "") : threadId;
    state.selectThread?.({ groupId });
    return {
      success: true,
      threadId: threadId.startsWith("group:") ? threadId : `group:${groupId}`,
      groupId,
    };
  }

  state.selectThread?.({ threadKey: threadId });
  return { success: true, threadId };
}

async function sendMessageAction(threadId: string, content: string): Promise<any> {
  if (!threadId || !content) {
    return { success: false, error: "Missing threadId or content" };
  }

  const state = useStore.getState();
  const senderId = parseJwt(state.apiToken || "").user_id;
  if (!senderId) {
    return { success: false, error: "No user logged in" };
  }

  let receiverId: string | undefined;
  let groupId: string | undefined;
  let normalizedThreadId = threadId;

  if (threadId.startsWith("group:") || isGroupId(threadId)) {
    groupId = threadId.startsWith("group:") ? threadId.replace("group:", "") : threadId;
    normalizedThreadId = threadId.startsWith("group:") ? threadId : `group:${groupId}`;
  } else if (threadId.startsWith("self:")) {
    receiverId = senderId;
  } else if (threadId.startsWith("dm:")) {
    const parsed = parseThreadId(threadId);
    if (!parsed) {
      return { success: false, error: "Invalid DM threadId" };
    }
    receiverId = parsed.user1 === senderId ? parsed.user2 : parsed.user1;
  } else {
    return { success: false, error: "Unsupported threadId format" };
  }

  if (normalizedThreadId === createSelfThreadKey(senderId)) {
    const existingThreads = await db.getGrps();
    const hasSelfThread = (existingThreads || []).some((thread: any) => {
      const candidate = String(thread?.id || thread?.groupId || "");
      return candidate === normalizedThreadId;
    });
    if (!hasSelfThread) {
      const now = Date.now();
      const selfThread = {
        id: normalizedThreadId,
        name: "Note to Self",
        description: null,
        communityId: null,
        ownerId: senderId,
        groupKey: null,
        members: null,
        membershipStatus: null,
        image: null,
        unread: 0,
        lastMessage: content,
        hiloMeta: null,
        isAnnouncementGroup: false,
        isDefaultGroup: false,
        isStandaloneGroup: false,
        isMute: true,
        isBlock: false,
        version: null,
        type: "selfChat",
        threadType: "selfChat",
        withUserId: senderId,
        createdAt: now,
        updatedAt: now,
      };
      state.setGroup?.(selfThread as any);
      await db.insertGrp(selfThread as any);
    }
  }

  const message = messageSchema.parse({
    id: generateUUID(),
    appId: appConfig.appId,
    type: MessageType.MESSAGE,
    status: "SENT",
    content: content,
    contentType: "TEXT",
    senderId,
    receiverId: receiverId || undefined,
    groupId: receiverId ? undefined : groupId || undefined,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    environment: state.environment === "hi" ? "hi" : undefined,
  });

  await services.threadService.sendMessage?.(message);

  return {
    success: true,
    threadId: normalizedThreadId,
    messageId: message.id,
    receiverId: receiverId || null,
    groupId: groupId || null,
  };
}

function decodeBase64ToUint8Array(dataBase64: string): Uint8Array {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function sendAttachmentMessageAction(
  threadId: string,
  filename: string,
  mimeType: string,
  dataBase64: string,
  replyToMessageId?: string
): Promise<any> {
  const trimmedThreadId = (threadId || "").trim();
  const trimmedFilename = (filename || "").trim();
  const trimmedMimeType = (mimeType || "").trim();
  const trimmedData = (dataBase64 || "").trim();

  if (!trimmedThreadId || !trimmedFilename || !trimmedMimeType || !trimmedData) {
    return {
      success: false,
      error:
        "Missing required fields: threadId, filename, mimeType, dataBase64",
    };
  }

  const state = useStore.getState();
  const senderId = parseJwt(state.apiToken || "").user_id;
  if (!senderId) {
    return { success: false, error: "No user logged in" };
  }

  let receiverId: string | undefined;
  if (trimmedThreadId.startsWith("self:")) {
    receiverId = senderId;
  } else if (trimmedThreadId.startsWith("dm:")) {
    const parsed = parseThreadId(trimmedThreadId);
    if (!parsed) {
      return { success: false, error: "Invalid DM threadId" };
    }
    receiverId = parsed.user1 === senderId ? parsed.user2 : parsed.user1;
  } else {
    return {
      success: false,
      error: "send_attachment_message currently supports DM/self threads only",
    };
  }

  let plaintext: Uint8Array;
  try {
    plaintext = decodeBase64ToUint8Array(trimmedData);
  } catch (e: any) {
    return { success: false, error: e?.message || "Invalid dataBase64 payload" };
  }

  if (!plaintext.length) {
    return { success: false, error: "Decoded attachment payload is empty" };
  }

  try {
    const encrypted = attachments.encrypt(plaintext);
    const uploadId = generateUUID();
    const presigned = await coreApi.getPresignedUrl(`attachment-${uploadId}.enc`);
    const uploadUrl = presigned.url;
    const objectUrl = stableObjectUrl(uploadUrl);

    const uploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      body: encrypted.data,
      headers: {
        "Content-Type": "application/octet-stream",
        "x-amz-acl": "public-read",
      },
    });

    if (!uploadResponse.ok) {
      return {
        success: false,
        error: `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
      };
    }

    const attachment = {
      id: generateUUID(),
      mimeType: trimmedMimeType,
      objectUrl,
      encryptedUrl: objectUrl,
      keyBase64: encrypted.key_base64,
      filename: trimmedFilename,
      size: plaintext.byteLength,
    };

    const message = messageSchema.parse({
      id: generateUUID(),
      appId: appConfig.appId,
      type: MessageType.MESSAGE,
      status: "SENT",
      content: JSON.stringify(attachment),
      contentType: "FILE",
      attachment,
      senderId,
      receiverId: receiverId || undefined,
      groupId: undefined,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      environment: state.environment === "hi" ? "hi" : undefined,
      replyToMessageId: (replyToMessageId || "").trim() || undefined,
    });

    await services.threadService.sendMessage?.(message);

    return {
      success: true,
      threadId: trimmedThreadId,
      messageId: message.id,
      receiverId: receiverId || null,
      filename: trimmedFilename,
      mimeType: trimmedMimeType,
    };
  } catch (e: any) {
    return {
      success: false,
      error: e?.message || "Failed to send attachment message",
    };
  }
}

async function createGroupAction(name: string): Promise<any> {
  const trimmed = (name || "").trim();
  if (!trimmed) {
    return { success: false, error: "Missing group name" };
  }

  const state = useStore.getState();
  const myUserId = parseJwt(state.apiToken || "").user_id;
  if (!myUserId) {
    return { success: false, error: "No user logged in" };
  }

  await initSwitchCore();
  const group = await coreGroups.createGroup(trimmed, null, myUserId);
  await db.saveThread(group);

  if (state.setGroup) {
    state.setGroup(group as Thread);
  } else {
    state.setThread?.(group as Thread);
  }

  const groupId = group.id.startsWith("group:") ? group.id.replace("group:", "") : group.id;
  const conversation = groupConversationIdentity(groupId);

  return {
    success: true,
    threadId: group.id,
    groupId,
    baseThreadKey: conversation.baseThreadKey,
    threadScopeKey: conversation.threadScopeKey,
    conversation,
    name: group.name,
  };
}

function groupConversationIdentity(
  groupId: string,
  options?: { environmentId?: string; channelId?: string; accessMode?: string; communityId?: string }
): any {
  const cleanGroupId = (groupId || "").replace(/^group:/, "");
  const state = useStore.getState() as any;
  const environmentId = options?.environmentId || "default";
  const channelId = options?.channelId || "default";
  const effectiveMode =
    options?.accessMode || (state.environment === "hi" ? "high" : "low");
  if (options?.communityId) {
    const lane = createCommunityChannelConversationLane(
      options.communityId,
      cleanGroupId,
      environmentId,
      channelId,
      effectiveMode
    );
    const baseThreadKey = lane.conversationId;
    const threadScopeKey = lane.laneId;
    return {
      kind: "community_channel",
      baseThreadKey,
      threadKey: threadScopeKey,
      threadScopeKey,
      communityId: options.communityId,
      scope: {
        kind: "community_channel",
        environmentId,
        channelId,
        effectiveMode,
        directThreadsAreScoped: true,
      },
    };
  }
  const lane = createGroupConversationLane(cleanGroupId, environmentId, channelId, effectiveMode);
  const baseThreadKey = lane.conversationId || createGroupThreadKey(cleanGroupId);
  const threadScopeKey = lane.laneId;
  return {
    kind: "group",
    baseThreadKey,
    threadKey: threadScopeKey,
    threadScopeKey,
    scope: {
      kind: "group",
      environmentId,
      channelId,
      effectiveMode,
      directThreadsAreScoped: true,
    },
  };
}

function communityConversationIdentity(
  communityId: string,
  options?: { environmentId?: string; channelId?: string; accessMode?: string }
): any {
  const state = useStore.getState() as any;
  const environmentId = options?.environmentId || "default";
  const channelId = options?.channelId || "default";
  const effectiveMode =
    options?.accessMode || (state.environment === "hi" ? "high" : "low");
  const baseThreadKey = `community:${communityId}`;
  const threadScopeKey = `community:${environmentId}:${channelId}:${effectiveMode}:${communityId}`;
  return {
    kind: "community",
    baseThreadKey,
    threadKey: threadScopeKey,
    threadScopeKey,
    communityId,
    scope: {
      kind: "community",
      environmentId,
      channelId,
      effectiveMode,
      directThreadsAreScoped: true,
    },
  };
}

async function addGroupMembersByHandleAction(threadId: string, handles: string[]): Promise<any> {
  if (!threadId) {
    return { success: false, error: "Missing threadId" };
  }
  if (!handles || handles.length === 0) {
    return { success: false, error: "handles array cannot be empty" };
  }

  const state = useStore.getState();
  const normalizedThreadId =
    threadId.startsWith("group:") || !isGroupId(threadId)
      ? threadId
      : `group:${threadId}`;

  let thread = (state.threadMap || {})[normalizedThreadId] as Thread | undefined;
  if (!thread) {
    try {
      thread = (await services.db.getGrp?.(normalizedThreadId)) as Thread | undefined;
    } catch (err) {
      console.warn("[Debug] Failed to load thread from db", err);
    }
  }

  if (!thread) {
    return { success: false, error: "Thread not found" };
  }

  const added: string[] = [];
  const missing: string[] = [];

  for (const handle of handles || []) {
    const resolved = await resolveContactByHandle(handle, { allowAdd: true });
    if (!resolved.contact) {
      missing.push(handle);
      continue;
    }

    const member: Member = {
      userId: resolved.contact.userId,
      role: "member",
      joinedAt: Date.now(),
    };

    try {
      await groupAddMember(member, thread);
      added.push(resolved.contact.userId);
    } catch (err) {
      console.warn("[Debug] Failed to add group member", err);
      missing.push(handle);
    }
  }

  const refreshedThread = (state.threadMap || {})[normalizedThreadId] as Thread | undefined;
  const memberCount = refreshedThread?.members?.length ?? thread.members?.length ?? added.length;

  return {
    success: missing.length === 0,
    threadId: normalizedThreadId,
    addedMembers: added.length,
    missingMembers: missing,
    memberCount,
  };
}

/**
 * Search for a user by full handle (username.discriminator)
 */
async function searchUser(handle: string): Promise<SearchUserResponse> {
  if (!handle || handle.trim().length === 0) {
    return {
      found: false,
      user: null,
      error: "Missing required parameter: username",
    };
  }

  const trimmed = handle.trim().toLowerCase();

  // Validate format: must contain "." for username.discriminator
  if (!trimmed.includes(".")) {
    return {
      found: false,
      user: null,
      error: "Invalid username format. Expected: username.discriminator",
    };
  }

  // Parse username and discriminator
  const dotIndex = trimmed.indexOf(".");
  const baseUsername = trimmed.substring(0, dotIndex);
  const discriminator = trimmed.substring(dotIndex + 1);

  // Validate discriminator is 4 digits
  if (discriminator.length !== 4 || !/^\d{4}$/.test(discriminator)) {
    return {
      found: false,
      user: null,
      error: "Invalid discriminator. Expected 4-digit number",
    };
  }

  // Check if user is logged in
  const state = useStore.getState();
  if (!state.apiToken) {
    return {
      found: false,
      user: null,
      error: "No user logged in",
    };
  }

  try {
    // Use api_find_user which takes the full handle
    const result = await api_find_user(trimmed);

    if (!result || !result.userId) {
      return {
        found: false,
        user: null,
        error: null,
      };
    }

    const userData = (result as any).user || {};
    return {
      found: true,
      user: {
        userId: result.userId,
        username: result.username || baseUsername,
        discriminator: result.discriminator || discriminator,
        publicKey:
          (result as any).publicKey ||
          (result as any).public_key ||
          (result as any).publicKeys?.[0]?.publicKey ||
          userData.publicKey ||
          userData.public_key ||
          userData.publicKeys?.[0]?.publicKey ||
          null,
      },
      error: null,
    };
  } catch (e: any) {
    // Check if it's a 404 not found
    if (e?.message?.includes("404") || e?.message?.toLowerCase().includes("not found")) {
      return {
        found: false,
        user: null,
        error: null,
      };
    }

    return {
      found: false,
      user: null,
      error: `Search failed: ${e?.message || "Unknown error"}`,
    };
  }
}

/**
 * Get the currently logged-in user
 */
async function currentUser(): Promise<CurrentUserResponse> {
  const state = useStore.getState();
  const tokenData = state.apiToken ? parseJwt(state.apiToken) : null;

  if (!state.apiToken || !tokenData?.user_id) {
    return {
      found: false,
      user: null,
      error: "No user logged in",
    };
  }

  return {
    found: true,
    user: {
      userId: tokenData.user_id,
      username: tokenData.username || null,
      discriminator: tokenData.discriminator || null,
      publicKey: null,
    },
    error: null,
  };
}

/**
 * Get all contacts from local storage
 */
async function contacts(): Promise<any[]> {
  try {
    const result = await db.getUsers();
    return result || [];
  } catch (e) {
    console.error("[Debug] Failed to get contacts:", e);
    return [];
  }
}

/**
 * Get all threads from local storage
 */
async function threads(): Promise<any[]> {
  try {
    const result = await db.getGrps();
    const current = await currentUser();
    const currentUserId = current.user?.userId || null;
    const normalized = (result || []).map((thread: any) => {
      const threadId = String(thread?.id || thread?.groupId || "");
      if (!currentUserId || threadId !== createSelfThreadKey(currentUserId)) {
        return thread;
      }

      return {
        ...thread,
        id: threadId,
        name: "Note to Self",
        type: thread.type || "selfChat",
        threadType: thread.threadType || thread.type || "selfChat",
        withUserId: thread.withUserId || thread.withUserID || currentUserId,
        withUserID: thread.withUserID || thread.withUserId || currentUserId,
        unread: thread.unread ?? thread.unreadCount ?? 0,
        unreadCount: thread.unreadCount ?? thread.unread ?? 0,
        isMute: thread.isMute ?? thread.isMuted ?? true,
        isMuted: thread.isMuted ?? thread.isMute ?? true,
      };
    });

    if (!currentUserId) {
      return normalized;
    }

    const noteToSelfId = createSelfThreadKey(currentUserId);
    const hasNoteToSelf = normalized.some((thread: any) => {
      const threadId = String(thread?.id || thread?.groupId || "");
      const withUserId = thread?.withUserId || thread?.withUserID || thread?.otherUserId;
      return threadId === noteToSelfId || withUserId === currentUserId;
    });
    if (hasNoteToSelf) {
      return normalized;
    }

    const selfMessages = await db.getMessagesByThreadId(noteToSelfId, undefined, 1);
    if (!Array.isArray(selfMessages) || selfMessages.length === 0) {
      return normalized;
    }

    const latestMessage = selfMessages[0] as any;
    return [
      {
        id: noteToSelfId,
        name: "Note to Self",
        type: "selfChat",
        threadType: "selfChat",
        withUserId: currentUserId,
        withUserID: currentUserId,
        createdAt: latestMessage.createdAt || Date.now(),
        updatedAt: latestMessage.updatedAt || latestMessage.createdAt || Date.now(),
        unread: 0,
        unreadCount: 0,
        lastMessage: latestMessage.content || null,
        lastMessagePreview: latestMessage.content || null,
        isMute: true,
        isMuted: true,
        isAnnouncementGroup: false,
        isDefaultGroup: false,
        isStandaloneGroup: false,
      },
      ...normalized,
    ];
  } catch (e) {
    console.error("[Debug] Failed to get threads:", e);
    return [];
  }
}

/**
 * Get all communities from local storage
 */
async function communities(): Promise<any[]> {
  try {
    const result = await db.getCommunities();
    return result || [];
  } catch (e) {
    console.error("[Debug] Failed to get communities:", e);
    return [];
  }
}

/**
 * Get all messages from local storage
 */
async function messages(threadKey?: string): Promise<any[]> {
  try {
    const normalizeMessage = (message: any) => {
      if (!message || typeof message !== "object") {
        return message;
      }

      const normalizedThreadId =
        message.threadId ||
        message.threadID ||
        message.threadKey ||
        message.thread_key ||
        null;

      return {
        ...message,
        threadId: normalizedThreadId,
      };
    };

    const loadMessages = async (key: string) => {
      if (key.startsWith("dm:") || key.startsWith("self:")) {
        return db.getMessagesByThreadId(key, undefined, 10000);
      }
      const groupId = key.startsWith("group:") ? key.replace(/^group:/, "") : key;
      return db.getMessagesByGroupId(groupId, undefined, 10000);
    };

    if (threadKey) {
      const result = await loadMessages(threadKey);
      return Array.isArray(result) ? result.map(normalizeMessage) : [];
    }

    const threads = await db.getGrps();
    const results: any[] = [];
    for (const thread of threads || []) {
      const legacyKey =
        (thread as any).threadKey ||
        (thread as any).thread_key ||
        (thread as any).groupId ||
        (thread as any).group_id;
      const key = legacyKey || thread.id;
      if (!key) continue;
      const items = await loadMessages(key);
      if (Array.isArray(items)) {
        results.push(...items.map(normalizeMessage));
      }
    }
    return results;
  } catch (e) {
    console.error("[Debug] Failed to get messages:", e);
    return [];
  }
}

/**
 * Get COMMUNITY_UPDATED messages from local storage
 */
async function communityUpdates(communityId?: string): Promise<any[]> {
  try {
    const allMessages = await messages();
    const filtered = allMessages.filter((msg: any) => {
      const type = (msg?.type || msg?.messageType || "").toString().toUpperCase();
      if (type !== "COMMUNITY_UPDATED") return false;
      if (!communityId) return true;
      return msg.communityId === communityId || msg.threadID === communityId || msg.threadId === communityId;
    });
    return filtered;
  } catch (e) {
    console.error("[Debug] Failed to get community updates:", e);
    return [];
  }
}

/**
 * Reprocess a COMMUNITY_UPDATED message by messageId (or latest for a communityId).
 */
async function reprocessCommunityUpdate(input: {
  messageId?: string;
  communityId?: string;
  forceContactRefresh?: boolean;
}): Promise<any> {
  try {
    const messageId = (input?.messageId || "").trim();
    const communityIdParam = (input?.communityId || "").trim();

    if (!messageId && !communityIdParam) {
      return { success: false, error: "Missing required field: messageId" };
    }

    const candidates = await communityUpdates(communityIdParam || undefined);
    const resolved = messageId
      ? candidates.find((msg: any) => (msg?.id || msg?.messageId) === messageId)
      : candidates.sort((a: any, b: any) => (b?.updatedAt || b?.createdAt || 0) - (a?.updatedAt || a?.createdAt || 0))[0];

    if (!resolved) {
      return { success: false, error: "Message not found" };
    }

    const state = useStore.getState() as any;
    const currentUserId =
      state.userId || (state.apiToken ? parseJwt(state.apiToken)?.user_id : undefined);
    if (!currentUserId) {
      return { success: false, error: "No user logged in" };
    }

    const receiverSecretKey =
      state.keyPair?.base64?.secretKey || state.keyPair?.base64?.secret_key;
    if (!receiverSecretKey) {
      return { success: false, error: "Missing receiver secret key" };
    }

    const senderId =
      resolved.senderId || resolved.senderID || resolved.sender_id || undefined;
    const receiverId =
      resolved.receiverId || resolved.receiverID || resolved.receiver_id || undefined;
    const encryptedContent =
      resolved.encryptedContent || resolved.encrypted_content || undefined;
    const nonce = resolved.nonce || undefined;

    if (!senderId || !encryptedContent || !nonce) {
      return { success: false, error: "Message missing senderId or encryptedContent" };
    }

    let threadKey =
      resolved.threadKey ||
      resolved.thread_key ||
      resolved.threadID ||
      resolved.threadId ||
      undefined;
    if (!threadKey && senderId && receiverId) {
      threadKey = createDirectThreadKey(senderId, receiverId);
    }

    const senderContact = state.contactsById?.[senderId];
    const senderPublicKey = resolveContactPublicKey(
      senderContact,
      resolved.senderKeyId || resolved.sender_key_id
    );

    const corePayload: Record<string, any> = {
      id: resolved.id || resolved.messageId,
      type: resolved.type || "COMMUNITY_UPDATED",
      appId: resolved.appId || resolved.appID || appConfig.appId,
      senderId,
      receiverId,
      communityId: resolved.communityId || resolved.community_id,
      threadKey,
      encryptedContent,
      nonce,
      contentType: resolved.contentType || resolved.content_type || "SYSTEM",
      createdAt: resolved.createdAt || resolved.created_at || Date.now(),
      updatedAt: resolved.updatedAt || resolved.updated_at || resolved.createdAt || Date.now(),
    };

    let coreResult = handleIncomingMessageJson(
      JSON.stringify(corePayload),
      currentUserId,
      receiverSecretKey,
      senderPublicKey || null
    );

    let decryptedContent = coreResult.message?.content || undefined;
    let decrypted = !!coreResult.decrypted;

    let resolvedCommunityId =
      communityIdParam ||
      resolved.communityId ||
      resolved.community_id ||
      undefined;

    if (!decrypted && input?.forceContactRefresh) {
      // No contact refresh path available in web debug; return core error details.
    }

    if (decryptedContent && !resolvedCommunityId) {
      try {
        const parsed = JSON.parse(decryptedContent);
        resolvedCommunityId = parsed?.communityId || parsed?.community_id;
      } catch {
        // ignore parse failure for preview-only
      }
    }

    if (decryptedContent) {
      try {
        await handleCommunityUpdatedPayload(decryptedContent, resolvedCommunityId);
      } catch (err) {
        console.warn("[Debug] Failed to handle COMMUNITY_UPDATED payload:", err);
      }
    }

    const communityPersisted = resolvedCommunityId
      ? !!(await db.getCommunity(resolvedCommunityId))
      : null;

    return {
      success: decrypted,
      messageId: resolved.id || resolved.messageId,
      communityId: resolvedCommunityId || null,
      senderId,
      receiverId,
      threadKey,
      decrypted,
      error: decrypted ? null : coreResult.error || "Failed to decrypt COMMUNITY_UPDATED payload",
      usedSenderKeyFingerprint: senderPublicKey
        ? senderPublicKey.trim().slice(0, 12).toUpperCase()
        : null,
      contactRefreshed: false,
      communityPersisted,
      payloadPreview: decryptedContent ? decryptedContent.slice(0, 240) : null,
    };
  } catch (e: any) {
    console.error("[Debug] Failed to reprocess community update:", e);
    return { success: false, error: e?.message || "Failed to reprocess community update" };
  }
}

/**
 * Get current auth token info
 */
function token(): { hasToken: boolean; claims: any | null; rawToken?: string | null } {
  const state = useStore.getState();
  const tokenData = state.apiToken ? parseJwt(state.apiToken) : null;
  return {
    hasToken: !!state.apiToken,
    rawToken: state.apiToken || null,
    claims: tokenData
      ? {
          userId: tokenData.user_id,
          username: tokenData.username,
          discriminator: tokenData.discriminator,
          appId: tokenData.app_id,
        }
      : null,
  };
}

function encryptionStatus(): {
  hasKeyPair: boolean;
  publicKey: string | null;
  secretKeyPresent: boolean;
  publicKeyFingerprint: string | null;
} {
  const state = useStore.getState() as any;
  const publicKey =
    state.keyPair?.base64?.publicKey ||
    state.keyPair?.base64?.public_key ||
    state.keyPair?.publicKey ||
    state.keyPair?.public_key ||
    null;
  const secretKey =
    state.keyPair?.base64?.secretKey ||
    state.keyPair?.base64?.secret_key ||
    state.keyPair?.secretKey ||
    state.keyPair?.secret_key ||
    null;
  return {
    hasKeyPair: !!publicKey && !!secretKey,
    publicKey,
    secretKeyPresent: !!secretKey,
    publicKeyFingerprint: publicKey ? publicKey.slice(0, 12) : null,
  };
}

function websocketStatus(): {
  state: string;
  lastError: string | null;
  lastMessage: string | null;
  acknowledgedMessageCount: number;
} {
  const state = useStore.getState() as any;
  const connected = !!state.wsConnected;
  return {
    state: connected ? "connected" : "disconnected",
    lastError: null,
    lastMessage: null,
    acknowledgedMessageCount: 0,
  };
}

function websocketDisconnect(): { success: boolean; state: string } {
  disconnectWebSocket();
  return { success: true, state: "disconnected" };
}

async function websocketConnect(): Promise<{ success: boolean; state: string; error?: string }> {
  const token = useStore.getState().apiToken;
  if (!token) {
    return { success: false, state: "disconnected", error: "No session token available" };
  }
  setToken(token);
  await connectWebSocket(token);
  return { success: true, state: "connecting" };
}

/**
 * Get group key info for conformance testing
 */
async function getGroupKeyInfo(groupId: string): Promise<GroupKeyInfoResponse> {
  if (!groupId) {
    return {
      groupId: "",
      currentKeyVersion: 0,
      totalKeys: 0,
      keyFingerprint: "",
      lastRotated: null,
      keys: [],
      error: "Missing required parameter: groupId",
    };
  }

  try {
    const allThreads = await threads();
    const thread = allThreads.find((t: any) => t.id === groupId);

    if (!thread) {
      return {
        groupId,
        currentKeyVersion: 0,
        totalKeys: 0,
        keyFingerprint: "",
        lastRotated: null,
        keys: [],
        error: `Group not found: ${groupId}`,
      };
    }

    const groupKeys = thread.groupKeys as Record<number, string> | undefined;
    const currentVersion = thread.currentKeyVersion as number | undefined || 1;

    // Compute fingerprints for all keys
    const keys: Array<{ version: number; fingerprint: string }> = [];
    if (groupKeys) {
      for (const [versionStr, keyB64] of Object.entries(groupKeys)) {
        keys.push({
          version: parseInt(versionStr, 10),
          fingerprint: keyB64.substring(0, 8).toUpperCase(),
        });
      }
      keys.sort((a, b) => a.version - b.version);
    }

    // Get current key fingerprint
    const currentKey = groupKeys?.[currentVersion] || thread.groupKey;
    const fingerprint = currentKey ? currentKey.substring(0, 8).toUpperCase() : "N/A";

    return {
      groupId,
      currentKeyVersion: currentVersion,
      totalKeys: keys.length || 1,
      keyFingerprint: fingerprint,
      lastRotated: null, // TODO: Track rotation timestamps
      keys,
      error: null,
    };
  } catch (e) {
    console.error("[Debug] Failed to get group key info:", e);
    return {
      groupId,
      currentKeyVersion: 0,
      totalKeys: 0,
      keyFingerprint: "",
      lastRotated: null,
      keys: [],
      error: `Failed to get group key info: ${e}`,
    };
  }
}

/**
 * Get app state (route, token status, user info)
 */
function appState(): AppStateResponse {
  const state = useStore.getState();
  const apiToken = state.apiToken;
  const activeThreadId =
    state.selectedThreadId ||
    (state.selection ? (state.selection as any).threadKey : undefined) ||
    null;
  const activeTab = (state as any).activeTab ?? null;

  if (!apiToken) {
    const screen: DebugAppScreen = "register";
    return {
      route: "register",
      screen,
      hasToken: false,
      userId: null,
      username: null,
      discriminator: null,
      activeThreadId,
      activeTab,
    };
  }

  const claims = parseJwt(apiToken);
  const hasDiscriminator = !!claims.discriminator;
  const route: "register" | "main" = hasDiscriminator ? "main" : "register";
  const screen: DebugAppScreen = activeThreadId
    ? "thread"
    : route === "main"
      ? "main"
      : "register";

  return {
    route,
    screen,
    hasToken: true,
    userId: claims.user_id || null,
    username: claims.username || null,
    discriminator: claims.discriminator || null,
    activeThreadId,
    activeTab,
  };
}

/**
 * Register a new user with username
 */
async function register(username: string): Promise<RegisterResponse> {
  if (!username || username.trim().length === 0) {
    return {
      success: false,
      error: "Missing required field: username",
    };
  }

  try {
    console.log("[Debug] Registering user:", username);

    // Ensure WASM is initialized
    await initSwitchCore();

    // Configure base URL/token if available
    const state = useStore.getState();
    if (typeof coreApi.configure === "function") {
      coreApi.configure(appConfig.apiUrl, state.apiToken);
    }

    // Ensure we have a public key; generate if missing
    let publicKeyBase64 = state.keyPair?.base64?.publicKey;
    if (!publicKeyBase64) {
      const kp = coreCrypto.boxKeypair();
      // Encode Uint8Array to base64
      publicKeyBase64 = btoa(String.fromCharCode(...kp.public_key));
    }

    // Call registration API
    const result = await coreApi.register(
      username.trim(),
      appConfig.appId,
      publicKeyBase64
    );

    console.log("[Debug] Register result:", result);

    const token = (result as any)?.token || (result as any)?.Token || result;
    if (!token || typeof token !== "string") {
      const errMsg = (result as any)?.error || "Registration did not return a token";
      return {
        success: false,
        error: errMsg,
      };
    }

    // Update store with new token
    const store = useStore.getState();
    store.setApiToken(token);
    store.setCommunityToken(token);

    // Kick off WebSocket connect after registering
    setToken(token);
    initializeWebSocket?.();

    // Parse the token to get user info
    const claims = parseJwt(token);
    const fullHandle = claims.full_handle || claims.username || null;
    const baseUsername =
      fullHandle && fullHandle.includes(".")
        ? fullHandle.split(".")[0]
        : fullHandle;
    const discriminator =
      claims.discriminator ||
      (fullHandle && fullHandle.includes(".")
        ? fullHandle.split(".")[1]
        : null);

    return {
      success: true,
      userId: claims.user_id,
      username: baseUsername || claims.user_id,
      discriminator,
    };
  } catch (e: any) {
    console.error("[Debug] Registration failed:", e);
    return {
      success: false,
      error: e?.message || "Registration failed",
    };
  }
}

function buildBasicAuthHeader(tokenValue: string): string {
  return `Basic ${btoa(`switch:${tokenValue}`)}`;
}

async function deviceLinkStart(): Promise<{
  success: boolean;
  code?: string;
  linkId?: string;
  expiresAt?: number;
  error?: string;
}> {
  try {
    const state = useStore.getState();
    if (!state.apiToken) {
      return { success: false, error: "Missing auth token" };
    }

    const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/device-link/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: buildBasicAuthHeader(state.apiToken),
      },
      body: JSON.stringify({}),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      return {
        success: false,
        error: payload?.error || payload?.message || `HTTP ${response.status}`,
      };
    }

    return {
      success: true,
      code: payload?.code,
      linkId: payload?.linkId,
      expiresAt: payload?.expiresAt,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to start device link" };
  }
}

async function deviceLinkComplete(
  code: string,
  platform: string = "web"
): Promise<{
  success: boolean;
  token?: string;
  deviceId?: string;
  status?: string;
  userId?: string;
  username?: string | null;
  discriminator?: string | null;
  error?: string;
}> {
  if (!code || code.trim().length === 0) {
    return { success: false, error: "Missing required field: code" };
  }

  try {
    await initSwitchCore();

    const state = useStore.getState();
    if (typeof coreApi.configure === "function") {
      coreApi.configure(appConfig.apiUrl, state.apiToken);
    }

    // Temporary key for KEY_SYNC handoff if server requests it.
    const kp = coreCrypto.boxKeypair();
    const tempPublicKey = btoa(String.fromCharCode(...kp.public_key));
    const tempSecretKey = btoa(String.fromCharCode(...kp.secret_key));

    const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/device-link/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: code.trim(),
        appId: appConfig.appId,
        platform,
        tempPublicKey,
      }),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      return {
        success: false,
        error: payload?.error || payload?.message || `HTTP ${response.status}`,
      };
    }

    const tokenValue = payload?.token;
    if (!tokenValue || typeof tokenValue !== "string") {
      return { success: false, error: "Link complete did not return a token" };
    }

    await applyLinkedDeviceSession(tokenValue, {
      publicKey: tempPublicKey,
      secretKey: tempSecretKey,
    });

    const claims = parseJwt(tokenValue);
    return {
      success: true,
      token: tokenValue,
      deviceId: payload?.deviceId || null,
      status: payload?.status || null,
      userId: claims?.user_id || null,
      username: claims?.username || null,
      discriminator: claims?.discriminator || null,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to complete device link" };
  }
}

async function listDevices(): Promise<{
  success: boolean;
  devices?: any[];
  error?: string;
}> {
  try {
    const state = useStore.getState();
    if (!state.apiToken) {
      return { success: false, error: "Missing auth token" };
    }

    const response = await fetch(`${appConfig.apiUrl.replace(/\/$/, "")}/devices`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: buildBasicAuthHeader(state.apiToken),
      },
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      return {
        success: false,
        error: payload?.error || payload?.message || `HTTP ${response.status}`,
      };
    }

    return {
      success: true,
      devices: Array.isArray(payload?.devices) ? payload.devices : [],
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to list devices" };
  }
}

function serverConfig(): ServerConfigResponse {
  return {
    apiBaseUrl: appConfig.apiUrl.replace(/\/$/, ""),
    websocketBaseUrl: appConfig.websocketUrl.replace(/\/$/, ""),
    appId: appConfig.appId,
  };
}

/**
 * Start an outgoing call for a thread using the same runtime flow as the UI call action.
 */
async function getCallTransport(): Promise<CallTransportResponse> {
  return {
    transport: currentCallTransport,
    supportedTransports: [...SUPPORTED_CALL_TRANSPORTS],
  };
}

async function setCallTransport(transport: string): Promise<{
  success: boolean;
  transport?: CallTransport;
  supportedTransports?: CallTransport[];
  error?: string;
}> {
  const normalized = (transport || "").trim().toLowerCase();
  if (!SUPPORTED_CALL_TRANSPORTS.includes(normalized as CallTransport)) {
    return {
      success: false,
      error: `Invalid call transport. Supported: ${SUPPORTED_CALL_TRANSPORTS.join(", ")}`,
      transport: currentCallTransport,
      supportedTransports: [...SUPPORTED_CALL_TRANSPORTS],
    };
  }
  return {
    success: true,
    transport: normalized as CallTransport,
    supportedTransports: [...SUPPORTED_CALL_TRANSPORTS],
  };
}

async function startCall(
  threadId: string,
  roomId?: string
): Promise<{
  success: boolean;
  threadId?: string;
  roomId?: string;
  transport?: CallTransport;
  error?: string;
}> {
  const trimmedThreadId = (threadId || "").trim();
  if (!trimmedThreadId) {
    return { success: false, error: "Missing required field: threadId" };
  }

  try {
    await initSwitchCore();

    const state = useStore.getState() as any;
    const callRoomId = resolveCallRoomId((roomId || "").trim() || trimmedThreadId);
    const tokenValue = state.apiToken;
    const claims = tokenValue ? parseJwt(tokenValue) : null;
    const myUserId = state.myUserId || claims?.user_id;

    if (!tokenValue) {
      return { success: false, error: "Missing auth token" };
    }

    const thread = typeof state.getThread === "function" ? state.getThread(trimmedThreadId) : state.threadMap?.[trimmedThreadId];
    const receiverId =
      thread?.members?.find((m: any) => m?.userId && m.userId !== myUserId)?.userId ||
      thread?.withUserId ||
      undefined;

    // Send CALL message to notify other participants
    const callMessage = {
      id: `call-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: MessageType.CALL,
      senderId: myUserId,
      receiverId,
      threadKey: trimmedThreadId,
      roomId: callRoomId,
      content: "",
      appId: appConfig.appId,
      timestamp: Date.now(),
      createdAt: Date.now(),
    };
    if ((services as any).threadService?.sendMessage) {
      (services as any).threadService.sendMessage(callMessage);
    }

    // Fetch token and join Jitsi
    const result = await fetchCallToken(callRoomId, tokenValue);
    const token = result.token;
    if (!token) {
      return { success: false, error: "Failed to fetch call token" };
    }
    if (!result.jitsiUrl) {
      return { success: false, error: "Missing jitsiUrl in call token response" };
    }

    if ((services as any).jitsi?.join) {
      (services as any).jitsi.join({ roomId: callRoomId, token, serverUrl: result.jitsiUrl });
    }

    return {
      success: true,
      threadId: trimmedThreadId,
      roomId: callRoomId,
      transport: currentCallTransport,
    };
  } catch (e: any) {
    console.error("[Debug] startCall failed:", e);
    return { success: false, error: e?.message || "Failed to start call" };
  }
}

/**
 * Get current call activity status.
 */
async function callStatus(): Promise<{
  callActive: boolean;
  roomId: string | null;
  transport: CallTransport;
}> {
  const status = (services as any).jitsi?.getStatus?.();
  if (status && typeof status.callActive === "boolean") {
    return {
      callActive: status.callActive,
      roomId: status.roomId ?? null,
      transport: currentCallTransport,
    };
  }
  return {
    callActive: false,
    roomId: null,
    transport: currentCallTransport,
  };
}

/**
 * Dispatch a core-defined debug action.
 */
async function dispatch(action: unknown): Promise<any> {
  try {
    await initSwitchCore();

    const actionJson =
      typeof action === "string" ? action : JSON.stringify(action ?? {});
    const parsed = (wasm as any).parse_debug_dispatch_action_json(actionJson) as {
      type?: string;
      username?: string;
      handle?: string;
      threadId?: string;
      content?: string;
      filename?: string;
      mimeType?: string;
      dataBase64?: string;
      replyToMessageId?: string;
      name?: string;
      handles?: string[];
    };

    switch (parsed?.type) {
      case "register_user":
        return await register(parsed.username || "");
      case "welcome_button_tapped":
        services.router.navigate("/create-user-view");
        return { success: true, action: parsed.type, route: "register" };
      case "add_contact_by_handle":
        return await addContactByHandleAction(parsed.handle || "");
      case "create_direct_thread":
        return await createDirectThreadByHandleAction(parsed.handle || "");
      case "open_thread":
        return openThreadAction(parsed.threadId || "");
      case "send_message":
        return await sendMessageAction(parsed.threadId || "", parsed.content || "");
      case "send_attachment_message":
        return await sendAttachmentMessageAction(
          parsed.threadId || "",
          parsed.filename || "",
          parsed.mimeType || "",
          parsed.dataBase64 || "",
          parsed.replyToMessageId || undefined
        );
      case "create_group":
        return await createGroupAction(parsed.name || "");
      case "add_group_members_by_handle":
        return await addGroupMembersByHandleAction(
          parsed.threadId || "",
          parsed.handles || []
        );
      case "logout":
        return await logout();
      default:
        return { success: false, error: "Unsupported action" };
    }
  } catch (e: any) {
    return { success: false, error: e?.message || "Dispatch failed" };
  }
}

/**
 * Create a community using the same flow as the UI.
 */
async function createCommunity(name: string, description?: string): Promise<any> {
  const trimmedName = (name || "").trim();
  if (!trimmedName) {
    return { success: false, error: "Missing required field: name" };
  }

  try {
    await initSwitchCore();
    const community = await createCommunityFlow({
      name: trimmedName,
      description: description?.trim() || null,
    });
    const conversation = communityConversationIdentity(community.id);
    return {
      success: true,
      community,
      communityId: community.id,
      baseThreadKey: conversation.baseThreadKey,
      threadScopeKey: conversation.threadScopeKey,
      conversation,
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to create community" };
  }
}

/**
 * Invite a member to a community using the UI flow.
 */
async function inviteCommunityMember(
  communityId: string,
  userId: string,
  role?: string
): Promise<any> {
  const trimmedCommunityId = (communityId || "").trim();
  const trimmedUserId = (userId || "").trim();
  if (!trimmedCommunityId || !trimmedUserId) {
    return { success: false, error: "Missing required fields: communityId, userId" };
  }

  const normalizedRole = normalizeCommunityRole(role, { allowOwner: false });
  if (normalizedRole.ok === false) {
    return { success: false, error: normalizedRole.error };
  }

  try {
    await initSwitchCore();
    const state = useStore.getState() as any;
    const community =
      state.communityMap?.[trimmedCommunityId] ||
      (await db.getCommunity(trimmedCommunityId));
    if (!community) {
      return { success: false, error: "Community not found" };
    }

    const updated = await inviteCommunityMemberFlow({
      community,
      userId: trimmedUserId,
      role: normalizedRole.value,
    } as any);
    return { success: true, community: updated };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to invite member" };
  }
}

/**
 * Create a community group using the UI flow.
 */
async function createCommunityGroup(
  communityId: string,
  name: string,
  description?: string
): Promise<any> {
  const trimmedCommunityId = (communityId || "").trim();
  const trimmedName = (name || "").trim();
  if (!trimmedCommunityId || !trimmedName) {
    return { success: false, error: "Missing required fields: communityId, name" };
  }

  try {
    await initSwitchCore();
    const state = useStore.getState() as any;
    const community =
      state.communityMap?.[trimmedCommunityId] ||
      (await db.getCommunity(trimmedCommunityId));
    if (!community) {
      return { success: false, error: "Community not found" };
    }

    const result = await createCommunityGroupFlow({
      community,
      name: trimmedName,
      description: description?.trim() || null,
    });

    return {
      success: true,
      community: result.community,
      group: result.group,
      groupId: result.group?.id,
      threadId: result.thread?.id,
      ...(result.group?.id ? (() => {
        const conversation = groupConversationIdentity(result.group.id, {
          communityId: result.community?.id || trimmedCommunityId,
        });
        return {
          baseThreadKey: conversation.baseThreadKey,
          threadScopeKey: conversation.threadScopeKey,
          conversation,
        };
      })() : {}),
    };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to create community group" };
  }
}

/**
 * Update a community member role using the UI flow.
 */
async function updateCommunityMemberRole(
  communityId: string,
  userId: string,
  role: string
): Promise<any> {
  const trimmedCommunityId = (communityId || "").trim();
  const trimmedUserId = (userId || "").trim();
  if (!trimmedCommunityId || !trimmedUserId) {
    return { success: false, error: "Missing required fields: communityId, userId" };
  }

  const normalizedRole = normalizeCommunityRole(role, { allowOwner: false });
  if (normalizedRole.ok === false) {
    return { success: false, error: normalizedRole.error };
  }
  if (!normalizedRole.value) {
    return { success: false, error: "Missing role" };
  }

  try {
    await initSwitchCore();
    const state = useStore.getState() as any;
    const community =
      state.communityMap?.[trimmedCommunityId] ||
      (await db.getCommunity(trimmedCommunityId));
    if (!community) {
      return { success: false, error: "Community not found" };
    }

    const updated = await updateCommunityMemberRoleFlow({
      community,
      userId: trimmedUserId,
      role: normalizedRole.value,
    } as any);
    return { success: true, community: updated };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to update role" };
  }
}

/**
 * Leave a community using the UI flow.
 */
async function leaveCommunity(communityId: string): Promise<any> {
  const trimmedCommunityId = (communityId || "").trim();
  if (!trimmedCommunityId) {
    return { success: false, error: "Missing required field: communityId" };
  }

  try {
    await initSwitchCore();
    const state = useStore.getState() as any;
    const community =
      state.communityMap?.[trimmedCommunityId] ||
      (await db.getCommunity(trimmedCommunityId));
    if (!community) {
      return { success: false, error: "Community not found" };
    }

    await leaveCommunityFlow({ community });
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || "Failed to leave community" };
  }
}

/**
 * Logout and clear all data
 */
async function logout(): Promise<LogoutResponse> {
  try {
    console.log("[Debug] Logging out user");

    await performClientLogout("debug_endpoint");

    return {
      success: true,
      message: "Logged out successfully",
    };
  } catch (e: any) {
    console.error("[Debug] Logout failed:", e);
    return {
      success: false,
      error: e?.message || "Logout failed",
    };
  }
}

/**
 * Get all data combined
 */
async function all() {
  const [contactsList, threadsList, messagesList] = await Promise.all([
    contacts(),
    threads(),
    messages(),
  ]);

  return {
    token: token(),
    users: { count: contactsList.length, data: contactsList },
    threads: { count: threadsList.length, data: threadsList },
    messages: { count: messagesList.length, data: messagesList },
  };
}

// Create the debug API object
const debugAPI: DebugAPI = {
  searchUser,
  currentUser,
  contacts,
  threads,
  messages,
  communityUpdates,
  reprocessCommunityUpdate,
  communities,
  token,
  encryptionStatus,
  websocketStatus,
  websocketDisconnect,
  websocketConnect,
  getGroupKeyInfo,
  all,
  appState,
  register,
  deviceLinkStart,
  deviceLinkComplete,
  listDevices,
  serverConfig,
  dispatch,
  startCall,
  callStatus,
  getCallTransport,
  setCallTransport,
  createCommunity,
  createCommunityGroup,
  inviteCommunityMember,
  leaveCommunity,
  updateCommunityMemberRole,
  logout,
};

/**
 * Initialize the debug API on window.__switchDebug
 * Only enabled in development mode
 */
export function initDebugAPI(): void {
  const isDev =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname.includes("127.0.0.1") ||
      import.meta.env.DEV);

  if (isDev) {
    (window as any).__switchDebug = debugAPI;
    console.log("[Debug] Debug API initialized at window.__switchDebug");
    console.log(
      "[Debug] Available methods: searchUser, currentUser, contacts, threads, messages, communityUpdates, reprocessCommunityUpdate, communities, token, encryptionStatus, websocketStatus, getGroupKeyInfo, all, appState, register, deviceLinkStart, deviceLinkComplete, listDevices, serverConfig, dispatch, startCall, callStatus, getCallTransport, setCallTransport, createCommunity, createCommunityGroup, inviteCommunityMember, leaveCommunity, updateCommunityMemberRole, logout"
    );
  }
}

// Export for direct usage
export { debugAPI };
export type { DebugAPI, SearchUserResponse, CurrentUserResponse };
